/**
 * Promotions Resource Client
 * Axios-based API client for promotion endpoints
 */

import { ApiClient } from './apiClient';
import { Promotion } from './checkout';

export interface PromotionCodeValidation {
    isValid: boolean;
    code: string;
    error?: string;
    promotion?: {
        id: string;
        name: string;
        type: string;
        value: number;
        currency: string;
    };
}

export class PromotionsResource {
    constructor(private apiClient: ApiClient) { }

    /**
     * Get applied promotions for a checkout session
     */
    async getAppliedPromotions(checkoutSessionId: string): Promise<Promotion[]> {
        return this.apiClient.get(`/api/v1/checkout-sessions/${checkoutSessionId}/promotions`);
    }

    /**
     * Apply a promotion code to a checkout session
     */
    async applyPromotionCode(
        checkoutSessionId: string,
        code: string
    ): Promise<{ success: boolean; error?: string; promotion?: Promotion }> {
        try {
            const response = await this.apiClient.post<{ success: boolean; error?: any; promotion?: Promotion }>(
                `/api/v1/checkout-sessions/${checkoutSessionId}/promotions/apply`,
                { code: code.trim() }
            );

            if (response.success) {
                return { success: true, promotion: response.promotion };
            } else {
                return { success: false, error: response.error?.message || 'Failed to apply promotion code' };
            }
        } catch (err) {
            // Try to extract error message from API response
            let errorMessage = 'Failed to apply promotion code';
            if (err && typeof err === 'object' && 'response' in err) {
                const apiError = err as any;
                if (apiError.response?.data?.error?.message) {
                    errorMessage = apiError.response.data.error.message;
                } else if (apiError.response?.data?.message) {
                    errorMessage = apiError.response.data.message;
                }
            }

            return { success: false, error: errorMessage };
        }
    }

    /**
     * Remove a promotion from a checkout session
     */
    async removePromotionCode(
        checkoutSessionId: string,
        promotionId: string
    ): Promise<{ success: boolean; error?: string }> {
        try {
            const response = await this.apiClient.delete<{ success: boolean; error?: any }>(
                `/api/v1/checkout-sessions/${checkoutSessionId}/promotions/${promotionId}`
            );

            if (response.success) {
                return { success: true };
            } else {
                return { success: false, error: response.error?.message || 'Failed to remove promotion' };
            }
        } catch (err) {
            // Try to extract error message from API response
            let errorMessage = 'Failed to remove promotion';
            if (err && typeof err === 'object' && 'response' in err) {
                const apiError = err as any;
                if (apiError.response?.data?.error?.message) {
                    errorMessage = apiError.response.data.error.message;
                } else if (apiError.response?.data?.message) {
                    errorMessage = apiError.response.data.message;
                }
            }

            return { success: false, error: errorMessage };
        }
    }

    /**
     * Validate a promotion code without applying it
     */
    async validatePromotionCode(
        checkoutSessionId: string,
        code: string
    ): Promise<PromotionCodeValidation> {
        try {
            const response = await this.apiClient.post<PromotionCodeValidation>(
                `/api/v1/checkout-sessions/${checkoutSessionId}/promotions/validate`,
                { code: code.trim() }
            );

            return response;
        } catch {
            return {
                isValid: false,
                code: code.trim(),
                error: 'Failed to validate promotion code',
            };
        }
    }
}
